import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  registerServiceWorker, 
  requestNotificationPermission, 
  subscribeToPush,
  isPushSupported 
} from '../services/pushNotifications';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [messages, setMessages] = useState([]);
  const wsRef = useRef(null);

  // Configure axios
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch user on mount
  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Auth error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, [token]);

  // WebSocket connection - handler is defined inline to avoid declaration order issues
  useEffect(() => {
    if (token && user) {
      // Fetch existing alerts on login
      const fetchAlerts = async () => {
        try {
          const response = await axios.get(`${API_URL}/alerts`);
          setAlerts(response.data);
        } catch (error) {
          console.error('Failed to fetch alerts:', error);
        }
      };
      fetchAlerts();

      let reconnectAttempts = 0;
      const maxReconnectAttempts = 10;
      let reconnectTimeout = null;
      let pingInterval = null;
      let pongTimeout = null;
      let isIntentionalClose = false;

      const connectWebSocket = () => {
        const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
        const socket = new WebSocket(`${wsUrl}/api/ws/${token}`);
        
        socket.onopen = () => {
          console.debug('[ws] connected');
          setWsConnected(true);
          reconnectAttempts = 0; // Reset on successful connect
          
          // Start ping interval to keep connection alive
          pingInterval = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({ type: 'ping' }));
              
              // Expect pong within 5 seconds
              pongTimeout = setTimeout(() => {
                console.debug('[ws] pong not received, reconnecting');
                // Force reconnect if no pong
                if (socket.readyState === WebSocket.OPEN) {
                  socket.close();
                }
              }, 5000);
            }
          }, 25000);
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Clear pong timeout on any message
            if (pongTimeout) {
              clearTimeout(pongTimeout);
              pongTimeout = null;
            }
            
            switch (data.type) {
              case 'connected':
                setOnlineUsers(data.online_users || []);
                break;
              case 'user_online':
                setOnlineUsers(prev => [...new Set([...prev, data.user_id])]);
                break;
              case 'user_offline':
                setOnlineUsers(prev => prev.filter(id => id !== data.user_id));
                break;
              case 'new_alert':
                // Prevent duplicate alerts
                setAlerts(prev => {
                  if (prev.some(a => a.id === data.alert.id)) {
                    return prev;
                  }
                  return [data.alert, ...prev];
                });
                // Audio sirens intentionally disabled — overlay uses vibration only.
                break;
              case 'alert_updated':
                setAlerts(prev => prev.map(a => a.id === data.alert.id ? data.alert : a));
                break;
              case 'alert_deleted':
                setAlerts(prev => prev.filter(a => a.id !== data.alert_id));
                break;
              case 'new_message':
                // Prevent duplicate messages - check by id and also by content+sender for temp messages
                setMessages(prev => {
                  // Check if message already exists
                  if (prev.some(m => m.id === data.message.id)) {
                    return prev;
                  }
                  // Remove any temp messages with same content from same sender
                  const filtered = prev.filter(m => {
                    if (m.isTemp && m.sender_id === data.message.sender_id && m._tempContent === data.message.content) {
                      return false;
                    }
                    return true;
                  });
                  return [...filtered, data.message];
                });
                break;
              case 'pong':
                // Pong received - connection is healthy
                break;
              default:
                console.log('Unknown WS message type:', data.type);
                break;
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        socket.onerror = () => {
          // Intentionally silent. The browser already logs WebSocket connection
          // failures to the console; duplicating it here just creates noise
          // every time a transient network blip or a normal reconnect cycle
          // fires the error event. Reconnection is handled in onclose below.
        };

        socket.onclose = () => {
          console.debug('[ws] disconnected');
          setWsConnected(false);
          
          // Clear intervals
          if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = null;
          }
          if (pongTimeout) {
            clearTimeout(pongTimeout);
            pongTimeout = null;
          }
          
          // Attempt reconnect unless intentionally closed
          if (!isIntentionalClose && reconnectAttempts < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000); // Exponential backoff, max 30s
            console.debug(`[ws] reconnecting in ${delay/1000}s (attempt ${reconnectAttempts + 1})`);
            reconnectTimeout = setTimeout(() => {
              reconnectAttempts++;
              const newSocket = connectWebSocket();
              wsRef.current = newSocket;
              setWs(newSocket);
            }, delay);
          }
        };

        return socket;
      };

      const socket = connectWebSocket();
      wsRef.current = socket;
      setWs(socket);

      // Initialize push notifications
      const initPush = async () => {
        if (isPushSupported()) {
          await registerServiceWorker();
          const permission = await requestNotificationPermission();
          if (permission === 'granted') {
            const result = await subscribeToPush(token);
            setPushEnabled(result.success);
            console.log('Push notifications:', result.success ? 'enabled' : 'failed');
          }
        }
      };
      initPush();

      return () => {
        isIntentionalClose = true;
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
        }
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.close();
        }
      };
    }
  }, [token, user]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    if (wsRef.current) {
      wsRef.current.close();
    }
  };

  const sendWsMessage = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    console.warn('[ws] not connected (state:', wsRef.current?.readyState, '), message not sent:', data);
    return false;
  }, []);

  // Helper function to check permissions
  const hasPermission = useCallback((permission) => {
    if (!user?.permissions) return false;
    return user.permissions[permission] === true;
  }, [user]);

  // Helper to check if user can send specific alert type
  const canSendAlertType = useCallback((alertType) => {
    if (!user?.permissions) return false;
    const allowedTypes = user.permissions.alert_types || [];
    return allowedTypes.includes('all') || allowedTypes.includes(alertType);
  }, [user]);

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    onlineUsers,
    alerts,
    setAlerts,
    messages,
    setMessages,
    sendWsMessage,
    ws,
    wsConnected,
    pushEnabled,
    isSecurityHead: user?.role === 'security_head',
    isAdmin: user?.permissions?.is_admin === true,
    permissions: user?.permissions || {},
    hasPermission,
    canSendAlertType
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
